import type {
  HotelBookingRoomOption,
  HotelOfferRoom,
  RoomCombinationGroup,
  RoomCombination,
} from "@/features/hotels/types/types";
import { isRefundable } from "@/features/hotels/utils/rooms";
import { DEFAULT_CURRENCY } from "@/utils/currency";

/**
 * Builds room combination groups for package booking from room slots.
 *
 * **Slots** — each element in `rooms` represents one room slot, i.e. a single
 * physical room that must be booked as part of the package. A two-room package
 * therefore has two slots.  Each slot exposes a list of `options` (room types
 * with different board bases and refundability variants).
 *
 * **Room combinations** — one combination is a concrete selection of exactly one
 * option per slot.  The resulting `RoomCombination` records the selected rooms,
 * their combined price, board basis, and a stable `id` built from the individual
 * room tokens.  Multiple combinations are grouped together when they share the
 * same set of room types (e.g. "Standard + Deluxe") but differ in board basis or
 * refundability.
 *
 * **Matched pairing** — a room type is *matched* when it appears in every slot.
 * For these types the algorithm pairs options that share the same rate code
 * (board basis + refundability), producing well-aligned combinations where both
 * rooms are of the same type and conditions.  Matched groups are preferred and
 * returned immediately when any exist.
 *
 * **Unmatched pairing** — when no room type spans all slots, the algorithm falls
 * back to *unmatched* pairing.  For each room type that is present in at least
 * one slot, the cheapest option of that type is used for the slots that carry it,
 * and the best-matching partner room (chosen by board-basis and refundability
 * overlap) fills the remaining slots. Duplicate groups are deduplicated and the
 * results are sorted by ascending minimum price.
 *
 * **Assumptions** — room option names are trimmed; board basis values are trimmed
 * and already uppercase. The algorithm relies on these invariants and does not
 * normalise them internally.
 */
export function createRoomCombinations(
  rooms: HotelOfferRoom[],
): RoomCombinationGroup[] {
  const validOptionsBySlot = rooms.map((slot) =>
    slot.options.filter(
      (opt) => opt.name && opt.price && opt.price.value > 0 && opt.boardBasis,
    ),
  );

  if (validOptionsBySlot.some((options) => options.length === 0)) {
    return [];
  }

  const roomTypesBySlot = validOptionsBySlot.map((options) =>
    groupBy(options, (opt) => opt.name.toLowerCase()),
  );

  const { matchedRoomTypes, unmatchedRoomTypes } =
    partitionRoomTypes(roomTypesBySlot);

  const matchedGroups = matchedRoomTypes
    .map((type) => createMatchingRoomGroup(type, roomTypesBySlot))
    .filter((group): group is RoomCombinationGroup => Boolean(group));

  if (matchedGroups.length > 0) {
    return sortGroupsByMinPrice(matchedGroups);
  }

  const unmatchedGroups = unmatchedRoomTypes
    .map((roomType) => createUnmatchedRoomGroup(roomType, roomTypesBySlot))
    .filter((group): group is RoomCombinationGroup => Boolean(group));

  return sortGroupsByMinPrice(deduplicateGroupsById(unmatchedGroups));
}

/**
 * Splits room types into:
 * - matched: present in every slot
 * - unmatched: missing in at least one slot
 */
function partitionRoomTypes(
  roomTypesBySlot: Map<string, HotelBookingRoomOption[]>[],
): { matchedRoomTypes: string[]; unmatchedRoomTypes: string[] } {
  const matchedRoomTypes: string[] = [];
  const unmatchedRoomTypes: string[] = [];

  const allRoomTypes = new Set(
    roomTypesBySlot.flatMap((roomTypes) => [...roomTypes.keys()]),
  );

  for (const roomType of allRoomTypes) {
    if (roomTypesBySlot.every((slot) => slot.has(roomType))) {
      matchedRoomTypes.push(roomType);
    } else {
      unmatchedRoomTypes.push(roomType);
    }
  }

  return { matchedRoomTypes, unmatchedRoomTypes };
}

/**
 * Creates a fallback mixed-room group for a room type that is not available in all slots.
 *
 * For each slot the room type is present in, the cheapest option of that type is used.
 * For every other slot, the best-matching partner room is chosen via
 * {@link findBestPartnerRoom}. This ensures the resulting combination always
 * spans every slot, regardless of how many slots there are.
 */
function createUnmatchedRoomGroup(
  roomTypeKey: string,
  roomTypesBySlot: Map<string, HotelBookingRoomOption[]>[],
): RoomCombinationGroup | null {
  const sourceSlotIndex = roomTypesBySlot.findIndex((slot) =>
    slot.has(roomTypeKey),
  );
  if (sourceSlotIndex === -1) return null;

  const sourceRoomOptions = roomTypesBySlot[sourceSlotIndex].get(roomTypeKey);
  if (!sourceRoomOptions?.length) return null;

  // Build one base room per slot:
  //   - slots that carry this room type → cheapest option of that type
  //   - other slots → best-matching partner room
  const baseRooms: HotelBookingRoomOption[] = [];
  for (let i = 0; i < roomTypesBySlot.length; i++) {
    if (roomTypesBySlot[i].has(roomTypeKey)) {
      const cheapest = cheapestByPrice(roomTypesBySlot[i].get(roomTypeKey)!);
      if (!cheapest) return null;
      baseRooms.push(cheapest);
    } else {
      const partner = findBestPartnerRoom(
        sourceRoomOptions,
        roomTypesBySlot,
        i,
      );
      if (!partner) return null;
      baseRooms.push(partner);
    }
  }

  // Collect rate codes from ALL options of each slot's room type, not just the
  // single base room. A base room is only the cheapest/best-match option, so
  // using it alone would silently drop board-basis/refundability combinations
  // that exist on other options of the same room type.
  const allRateCodes = new Set(
    baseRooms
      .flatMap((baseRoom, idx) => {
        const key = baseRoom.name.toLowerCase();
        return (roomTypesBySlot[idx].get(key) ?? [baseRoom]).filter(
          (r) => r.boardBasis,
        );
      })
      .map(getRateCode),
  );

  const combinations: RoomCombination[] = [];

  for (const rateCode of allRateCodes) {
    const [boardBasisCode, refundability] = rateCode.split("_");
    const isNonRefundable = refundability === "NR";

    const rooms = baseRooms.map((room, idx) =>
      pickRoomForRate(room, roomTypesBySlot[idx], {
        boardBasis: boardBasisCode,
        refundable: !isNonRefundable,
      }),
    );

    combinations.push(buildRoomCombination(rooms, boardBasisCode));
  }

  return buildGroupFromCombinations(combinations);
}

/**
 * Creates a room group when the same room type exists in all slots.
 */
function createMatchingRoomGroup(
  roomTypeKey: string,
  roomTypesBySlot: Map<string, HotelBookingRoomOption[]>[],
): RoomCombinationGroup | null {
  const optionsBySlot = roomTypesBySlot.map(
    (slot) => slot.get(roomTypeKey) ?? [],
  );

  if (optionsBySlot.some((opts) => opts.length === 0)) {
    return null;
  }

  const ratesBySlot = optionsBySlot.map((options) => {
    const byRate = groupBy(options, getRateCode);
    const uniqueRates = new Map<string, HotelBookingRoomOption>();
    for (const [key, opts] of byRate) {
      const cheapest = cheapestByPrice(opts);
      if (cheapest) uniqueRates.set(key, cheapest);
    }
    return uniqueRates;
  });

  const allRateCodes = new Set(ratesBySlot.flatMap((slot) => [...slot.keys()]));
  const combinations: RoomCombination[] = [];

  for (const rateCode of allRateCodes) {
    if (!ratesBySlot.every((slot) => slot.has(rateCode))) continue;

    const rooms = ratesBySlot.map((slot) => slot.get(rateCode)!);

    combinations.push(buildRoomCombination(rooms, rooms[0].boardBasis));
  }

  return buildGroupFromCombinations(combinations);
}

/**
 * Selects the most suitable room from a target slot to pair with `sourceRoomOptions`.
 *
 * Preference is given to options sharing the highest number of rate codes
 * (board basis + refundability) with the source options. Ties are resolved by cheapest price.
 */
function findBestPartnerRoom(
  sourceRoomOptions: HotelBookingRoomOption[],
  roomTypesBySlot: Map<string, HotelBookingRoomOption[]>[],
  targetSlotIndex: number,
): HotelBookingRoomOption | null {
  const sourceRateCodes = new Set(sourceRoomOptions.map(getRateCode));

  if (targetSlotIndex >= roomTypesBySlot.length) return null;

  const partnerSlot = roomTypesBySlot[targetSlotIndex];

  let bestMatch: HotelBookingRoomOption | null = null;
  let bestScore = -1;

  for (const partnerOptions of partnerSlot.values()) {
    const partnerRateCodes = new Set(partnerOptions.map(getRateCode));

    const score = [...sourceRateCodes].filter((code) =>
      partnerRateCodes.has(code),
    ).length;

    const candidate = cheapestByPrice(partnerOptions);
    if (
      score > bestScore ||
      (score === bestScore &&
        candidate !== null &&
        (bestMatch === null || getPrice(candidate) < getPrice(bestMatch)))
    ) {
      bestScore = score;
      bestMatch = candidate;
    }
  }

  if (!bestMatch) {
    bestMatch = cheapestByPrice([...partnerSlot.values()].flat());
  }

  return bestMatch;
}

/**
 * Picks the most appropriate option matching the target board basis and refundability
 * from the room type options in a slot.
 */
function pickRoomForRate(
  fallbackRoom: HotelBookingRoomOption,
  slotRoomTypes: Map<string, HotelBookingRoomOption[]>,
  targetConditions: { boardBasis: string; refundable: boolean },
): HotelBookingRoomOption {
  const roomTypeKey = fallbackRoom.name.toLowerCase();
  const roomTypeOptions = slotRoomTypes.get(roomTypeKey) ?? [];

  const exactMatches = roomTypeOptions.filter(
    (opt) =>
      opt.boardBasis === targetConditions.boardBasis &&
      isRefundable(opt) === targetConditions.refundable,
  );
  if (exactMatches.length)
    return cheapestByPrice(exactMatches) ?? exactMatches[0];

  const boardBasisMatches = roomTypeOptions.filter(
    (opt) => opt.boardBasis === targetConditions.boardBasis,
  );
  if (boardBasisMatches.length)
    return cheapestByPrice(boardBasisMatches) ?? boardBasisMatches[0];

  return cheapestByPrice(roomTypeOptions) ?? fallbackRoom;
}

/**
 * Builds a normalized room combination payload from selected room options.
 */
function buildRoomCombination(
  rooms: HotelBookingRoomOption[],
  boardBasis: string,
): RoomCombination {
  return {
    id: rooms.map((room) => room.roomToken).join("|"),
    boardBasis,
    isRefundable: rooms.every((room) => isRefundable(room)),
    totalPrice: rooms.reduce((sum, room) => sum + getPrice(room), 0),
    currency: rooms[0].price?.currency ?? DEFAULT_CURRENCY,
    rooms,
  };
}

/**
 * Deduplicates groups by id, keeping the group with the lower minPrice.
 */
function deduplicateGroupsById(
  groups: RoomCombinationGroup[],
): RoomCombinationGroup[] {
  const uniqueGroups = new Map<string, RoomCombinationGroup>();

  for (const group of groups) {
    const existing = uniqueGroups.get(group.id);
    if (!existing || group.minPrice < existing.minPrice) {
      uniqueGroups.set(group.id, group);
    }
  }

  return [...uniqueGroups.values()];
}

/**
 * Returns groups sorted by minPrice in ascending order.
 */
function sortGroupsByMinPrice(
  groups: RoomCombinationGroup[],
): RoomCombinationGroup[] {
  return groups.sort((a, b) => a.minPrice - b.minPrice);
}

/**
 * Converts raw combinations into a group payload used by the UI.
 */
function buildGroupFromCombinations(
  combinations: RoomCombination[],
): RoomCombinationGroup | null {
  if (combinations.length === 0) return null;

  const sorted = [...combinations].sort((a, b) => a.totalPrice - b.totalPrice);
  const cheapestCombination = sorted[0];

  const roomNames = cheapestCombination.rooms.map((room) => room.name);

  return {
    id: roomNames.join("|"),
    displayName: formatRoomCombinationName(roomNames),
    combinations: sorted,
    minPrice: cheapestCombination.totalPrice,
  };
}

/**
 * Creates a human-readable room combination name, collapsing duplicate names
 * into the `Nx Name` format.
 */
function formatRoomCombinationName(roomNames: string[]): string {
  const counts = new Map<string, number>();

  for (const name of roomNames) {
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }

  const parts: string[] = [];
  for (const [name, count] of counts) {
    parts.push(count > 1 ? `${count}x ${name}` : name);
  }

  return parts.join(" • ");
}

/**
 * Groups items by a computed string key.
 */
function groupBy<T>(items: T[], keyFn: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    const existing = map.get(key);
    if (existing) {
      existing.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  return map;
}

/**
 * Encodes board basis and refundability to a stable rate code key.
 */
function getRateCode(opt: HotelBookingRoomOption): string {
  return `${opt.boardBasis}_${isRefundable(opt) ? "R" : "NR"}`;
}

/**
 * Safely resolves room price value.
 */
function getPrice(opt: HotelBookingRoomOption): number {
  return opt.price?.value ?? 0;
}

/**
 * Returns the cheapest option from a list, or null when the list is empty.
 */
function cheapestByPrice(
  options: HotelBookingRoomOption[],
): HotelBookingRoomOption | null {
  if (options.length === 0) return null;
  return options.reduce((min, opt) =>
    getPrice(opt) < getPrice(min) ? opt : min,
  );
}
